import { AdminScope } from '@pubstack/common/src/scope'
import { AdminSiteTestBidder, AdminSiteTestBidderUpdate } from '@pubstack/common/src/pbs/pbs-test-bidder'
import { AdminSite } from '@pubstack/common/src/site'
import { sortByName } from '@pubstack/common/src/sort'
import { useQueryClient } from '@tanstack/react-query'
import { buildUrlWithSearchParams } from '~/utils/url'
import { MutationToasts, useMutation, useQuery } from '../api-client'
import { ADMIN_SCOPE_TEST_BIDDER_QUERY_KEYS } from './pbs-scope-config.api'

export const ADMIN_SITES_QUERY_KEYS = (scopeId: string) => ['admin', 'scopes', scopeId, 'tags']

//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
export const useAdminSite = (scopeId: string, siteId?: string) => {
  return useQuery<AdminSite>({ url: `/admin/scopes/${scopeId}/sites/${siteId}`, keys: [...ADMIN_SITES_QUERY_KEYS(scopeId), siteId ?? ''], enabled: !!scopeId && !!siteId })
}

export const ADMIN_SITE_TEST_BIDDER_QUERY_KEYS = (scopeId: string, siteId: string) => [...ADMIN_SITES_QUERY_KEYS(scopeId), siteId, 'test-bidder']

export const useAdminSiteTestBidder = (scopeId: string, siteId?: string) => {
  return useQuery<AdminSiteTestBidder>({
    url: `/admin/scopes/${scopeId}/sites/${siteId}/test-bidder`,
    keys: ADMIN_SITE_TEST_BIDDER_QUERY_KEYS(scopeId, siteId ?? ''),
    enabled: !!scopeId && !!siteId,
  })
}

export const useAdminSites = (scopeId: AdminScope['id'], search?: string) => {
  const url = buildUrlWithSearchParams(`/admin/scopes/${scopeId}/sites`, { search })
  const queryResult = useQuery<AdminSite[]>({ url, keys: [...ADMIN_SITES_QUERY_KEYS(scopeId), { search }], enabled: !!scopeId, select: sortByName })

  return { ...queryResult, data: queryResult.data || [] }
}

export const useAllAdminSites = (search?: string) => {
  const url = buildUrlWithSearchParams(`/admin/sites`, { search })
  const queryResult = useQuery<AdminSite[]>({ url, keys: [...ADMIN_SITES_QUERY_KEYS('all'), { search }], keepPreviousDataEnabled: true, select: sortByName })

  return { ...queryResult, data: queryResult.data || [] }
}

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateAdminSite = (scopeId: AdminScope['id'], toasts: MutationToasts = {}) => {
  return useMutation<AdminSite>({
    method: 'post',
    url: `/admin/scopes/${scopeId}/sites`,
    collectionKeys: ADMIN_SITES_QUERY_KEYS(scopeId),
    ...toasts,
  })
}

export const useUpdateAdminSite = (scopeId: AdminScope['id'], siteId: string, isOptimistic: boolean, toasts: MutationToasts = {}) => {
  return useMutation<AdminSite>({
    method: 'put',
    url: `/admin/scopes/${scopeId}/sites/${siteId}`,
    collectionKeys: ADMIN_SITES_QUERY_KEYS(scopeId),
    resourceId: siteId,
    isOptimistic,
    ...toasts,
  })
}

export const useUpdateAdminSiteTestBidder = (scopeId: AdminScope['id'], siteId: string, toasts: MutationToasts = {}) => {
  const queryClient = useQueryClient()
  const mutation = useMutation<AdminSiteTestBidderUpdate, AdminSiteTestBidder>({
    method: 'put',
    url: `/admin/scopes/${scopeId}/sites/${siteId}/test-bidder`,
    collectionKeys: ADMIN_SITE_TEST_BIDDER_QUERY_KEYS(scopeId, siteId),
    ...toasts,
  })
  // The scope page lists the individually enabled sites, so it must refetch after a site toggle
  const mutateAsync: typeof mutation.mutateAsync = async (...args) => {
    const result = await mutation.mutateAsync(...args)
    await queryClient.invalidateQueries({ queryKey: ADMIN_SCOPE_TEST_BIDDER_QUERY_KEYS(scopeId) })
    return result
  }
  return { ...mutation, mutateAsync }
}

export const useDeleteAdminSite = (scopeId: AdminScope['id'], siteId: string, toasts: MutationToasts = {}) => {
  return useMutation<AdminSite>({
    method: 'delete',
    url: `/admin/scopes/${scopeId}/sites/${siteId}`,
    collectionKeys: ADMIN_SITES_QUERY_KEYS(scopeId),
    resourceId: siteId,
    ...toasts,
  })
}
